import { Component, EventEmitter, OnInit, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as Moment from 'moment-timezone';
import { of, Subject } from 'rxjs';
import {  switchMap, take, takeUntil } from 'rxjs/operators';
import { FusionAuthService } from '../../../../../shared/services/fusion-auth';
import { DateHelperService, PWADetectorService, ScreenService } from '../../../../../shared/services/general';
import { ReportsService } from '../../../../../shared/services/ocl';
import { ObjectUtils } from '../../../../../shared/utils';
import { Client } from '../../../../shared/models/order-entry/client';
import { OrdersService } from '../../../../shared/services/orders.service';
import {
    CumulativeOrderWrapper,
    CumulativeResultsCollection,
    OrderPatientInformation,
} from '../../models/cumulative-result';
import { ResultType } from '../../models/results';

const tz = Moment.tz.guess();

@Component({
    selector: 'app-cumulative-results',
    templateUrl: './cumulative-results.component.html',
    styleUrls: ['./cumulative-results.component.scss'],
})
export class CumulativeResultsComponent implements OnInit {
    public isLoading = false;
    public orderInfo: OrderPatientInformation = new OrderPatientInformation();
    public clientSelector: FormControl;
    public startDateControl: FormControl;
    public endDateControl: FormControl;
    public records = 5;
    public selectedClient: number = -1;
    public clinicalResultsVisible = false;
    public clinicalResultsLoading = false;
    public microbiologyResultsVisible = false;
    public microbiologyResultsLoading = false;
    public toxicologyResultsVisible = false;
    public toxicologyResultsLoading = false;
    public selectedTab = -1;

    public clinicalResults: CumulativeResultsCollection[] = [];
    public clinicalRows: any[] = [];
    public clinicalResultsColumns: any[] = [];
    public clinicalGraph: any[] = [];
    public clinicalSeriesGraph: any[] = [];

    public toxicologyResults: CumulativeResultsCollection[] = [];
    public toxicologyRows: any[] = [];
    public toxicologyResultsColumns: any[] = [];

    public microbiologyResults: CumulativeResultsCollection[] = [];
    public microbiologyRows: any[] = [];
    public microbiologyResultsColumns: any[] = [];
    public isPhoneOrTablet: boolean = false;
    public visibleScroll = false;
    private onDestroy$: Subject<void> = new Subject<void>();
    private orderId: number = -1;
    public user: any;
    public role: string = '';
    public clientIds: number[] = [];
    public filterClients = new EventEmitter();
    public startDate: any;
    public endDate: any;

    constructor(
        private ordersService: OrdersService,
        private route: ActivatedRoute,
        private dateHelper: DateHelperService,
        private fusionAuthService: FusionAuthService,
        private renderer: Renderer2,
        private pwaDetectorService: PWADetectorService,
        private reportsService: ReportsService,
        private screen: ScreenService,
    ) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);

        const end = new Date();
        end.setHours(23, 59, 59, 999);

        this.clientSelector = new FormControl({
            value: null,
        });
        this.startDateControl = new FormControl({
            value: Moment(start).tz(tz),
        });
        this.endDateControl = new FormControl({
            value: Moment(end).tz(tz),
        });
    }

    public ngOnInit() {
        this.handleResponsive();
        this.clientSelector.valueChanges
            .pipe(
                takeUntil(this.onDestroy$)
            )
            .subscribe((client: Client) => {
                this.selectedClient = client.id;
            });

        const request = this.route.paramMap.pipe(
            switchMap(params => {
                if (params.get('id')) {
                    this.isLoading = true;
                    // @ts-ignore
                  this.orderId = +params.get('id');

                    return this.ordersService.getOrderPatientInformation(this.orderId);
                } else {
                    return of(null);
                }
            }),
        );


      request.pipe(take(1))
        // @ts-ignore
            .subscribe((order: OrderPatientInformation) => {
                this.isLoading = false;
                this.orderInfo = order;
                this.ordersService
                    .getOrderTestsTypes(this.orderId)
                    .pipe(take(1))
                    .subscribe(response => {
                        this.isLoading = false;
                        this.clinicalResultsVisible = response.hasClinicalResult;
                        this.microbiologyResultsVisible = response.hasMicrobiologyResult;
                        this.toxicologyResultsVisible = response.hasToxicologyResult;

                        this.selectedTab = this.clinicalResultsVisible
                            ? 0
                            : this.toxicologyResultsVisible
                                ? 2
                                : this.microbiologyResultsVisible
                                    ? 1
                                    : -1;
                        this.searchResults();
                    });
            });
    }

    private handleResponsive() {
        this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
        this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
        });
    }

    public get PatientFullName() {
        return this.orderInfo ? `${this.orderInfo.patientLastName}, ${this.orderInfo.patientFirstName} ${this.orderInfo.patientMiddleName
            ? this.orderInfo.patientMiddleName : ''} ` : '';
    }

    public get DOB() {
      if (!this.orderInfo.patientBirthDate) return '';
        return this.orderInfo ? `(${this.dateHelper.dateWithFormatWithoutUtc(this.orderInfo.patientBirthDate, 'LL')})` : '';
    }

    public get Gender() {
        return this.orderInfo ? this.orderInfo.patientGender : '';
    }

    public get PatientAddress() {
        return this.orderInfo ? `${this.orderInfo.patientAddress ? this.orderInfo.patientAddress + ',' : ''}
        ${this.orderInfo.patientCity ? this.orderInfo.patientCity + ',' : ''} ${this.orderInfo.patientState} ${this.orderInfo.patientZipCode}` : '';
    }

    public get start() {
        return Moment(this.startDateControl.value).toDate();
    }

    public get end() {
        return Moment(this.endDateControl.value).toDate();
    }

    public search() {
        if (!this.selectedClient) {
            this.filterClients.emit();
        }
        this.searchResults();
    }

    private searchResults() {
        if (!this.toxicologyResultsLoading && !this.clinicalResultsLoading && !this.microbiologyResultsLoading) {
            const clientsIds = this.clientSelector.value;
            const startDate = this.startDateControl.value;
            const endDate = this.endDateControl.value;
            const records = this.records;

            this.loadClinicalResults(startDate, endDate, records);
            this.loadToxicologyResults(startDate, endDate, records);
            this.loadMicrobiologyResults(startDate, endDate, records);
        }
    }

    public clinicalSelection(e: any) {
        this.clinicalGraph = [];
        if (e.selectedRowKeys.length === 0) {
            this.visibleScroll = false;
        }
        this.clinicalSeriesGraph = e.selectedRowsData.map((data: any) => {
            return { name: data.name, valueField: `value_${data.name}` };
        });
        const collectionDates = this.clinicalResults.slice().reverse().map(cr => cr.collectionDate);
        collectionDates.forEach(date => {
            const dateResults = this.clinicalResults.filter(cr => Moment(date).isSame(cr.collectionDate));
            if (dateResults.length !== 0) {
                const lastOrder = dateResults[0].orders?.sort((a, b) => {
                    // @ts-ignore
                    if (isNaN(a.code) || isNaN(b.code)) {
                        return -1;
                    }
                    // @ts-ignore
                  return +a.code + +b.code;
                })[0];
                const graphResult = {
                    collectionDate: date,
                };
                for (const name of this.clinicalSeriesGraph.map(s => s.name)) {
                    if (lastOrder) {
                        const index = lastOrder.results?.findIndex(r => r.name === name);
                        if (index === -1) {
                          // @ts-ignore
                            graphResult[`value_${name}`] = 0;
                        } else {
                            // @ts-ignore
                            if ((lastOrder.results[index] as ClinicalCumulativeResult).resultType === ResultType.ValueValue && !isNaN(lastOrder.results[index].result)) {
                              // @ts-ignore
                                graphResult[`value_${name}`] = lastOrder.results[index].result;
                            } else {
                              // @ts-ignore
                                graphResult[`value_${name}`] = 0;
                            }
                        }
                    }
                }
                // @ts-ignore
                graphResult.collectionDate = new Date(graphResult.collectionDate).getTime();
                this.clinicalGraph.push(graphResult);
            }
        });
    }

    public graphTooltip(info: any) {

        const cumulativeResult = this.clinicalResults.filter(cr => Moment(info.argument).isSame(cr.collectionDate))[0];
        const orders = cumulativeResult.orders?.filter(o => o.results?.some(r => r.name === info.seriesName));

        let text = ``;

        orders?.forEach(o => {
            text += `<u>${o.code}</u> <br>`;
            text += `${Moment(o.date).format('LL')} <br>`;
            // @ts-ignore
            text += `${o.results.filter(r => r.name === info.seriesName)[0].result} ` +
                // @ts-ignore
                `${o.results.filter(r => r.name === info.seriesName)[0].unit} <br>`;
            text += `<br>`;
        });

        return text;
    }

    private loadClinicalResults(startDate: any, endDate: any, records: number) {
        if (this.clinicalResultsVisible) {
            this.clinicalResultsLoading = true;
            this.ordersService.getCumulativeResults(
                this.orderInfo.id,
                this.selectedClient,
                this.role === 'Physician' && this.user.data ? this.user.data.physicians : [],
                startDate.value ? startDate.value : startDate,
                endDate.value ? endDate.value : endDate,
                records, 'clinical')
                .pipe(take(1))
                .subscribe((results: CumulativeResultsCollection[]) => {
                    this.clinicalResultsLoading = false;
                    this.clinicalResults = results;

                    this.clinicalResultsColumns = this.generateColumns(this.clinicalResults);

                    this.clinicalRows = this.generateRows(results, this.clinicalResultsColumns.map(c => c.columns).length);
                });
        }
    }

    public clinicalRow(e: any) {
        e.cells.forEach((cell: any) => {
            if (cell.rowType && cell.columnIndex && cell.cellElement) {
                if (cell.rowType === 'data' && cell.columnIndex > 1) {
                    this.renderer.setProperty(cell.cellElement, 'textContent', 'NOT ORDERED');
                }
            }
        });

        if (e.data) {
            e.columns.forEach((column: any) => {
                // @ts-ignore
              const order = this.getOrder(ObjectUtils.flatten(this.clinicalResults?.map(cr => cr.orders)), column.caption);
                if (order) {
                    const index = order.results.findIndex(r => r.name === e.data.name);
                    if (index !== -1) {
                        const cellValue = `${order.results[index].result} ${(order.results[index] as ClinicalCumulativeResult).unit}`;
                        const columnIndex = e.columns.findIndex((c: any) => c.caption === column.caption);
                        const cellIndex = e.cells.findIndex((cell: any) => cell.columnIndex === columnIndex);
                        this.renderer.setProperty(e.cells[cellIndex].cellElement, 'textContent', cellValue);
                    }
                }
            });
        }
    }

    private loadToxicologyResults(startDate: any, endDate: any, records: number) {
        if (this.toxicologyResultsVisible) {
            this.toxicologyResultsLoading = true;
            this.ordersService.getCumulativeResults(
                this.orderInfo.id,
                this.selectedClient,
                this.role === 'Physician' && this.user.data ? this.user.data.physicians : [],
                startDate.value ? startDate.value : startDate,
                endDate.value ? endDate.value : endDate, records,
                'toxicology'
            )
                .pipe(takeUntil(this.onDestroy$)).subscribe((results: CumulativeResultsCollection[]) => {
                this.toxicologyResultsLoading = false;
                this.toxicologyResults = results;

                this.toxicologyResultsColumns = this.generateColumns(this.toxicologyResults);

                this.toxicologyRows = this.generateRows(results, this.toxicologyResultsColumns.map(c => c.columns).length);
            });
        }
    }

    public toxicologyRow(e: any) {
        e.cells.forEach((cell: any) => {
            if (cell.rowType === 'data' && cell.columnIndex !== 0 && cell.cellElement !== undefined) {
                this.renderer.setProperty(cell.cellElement, 'textContent', 'NOT ORDERED');
            }
        });

        if (e.data) {
            e.columns.forEach((column: any) => {
                // @ts-ignore
              const order = this.getOrder(ObjectUtils.flatten(this.toxicologyResults.map(cr => cr.orders)), column.caption);
                if (order) {
                    const index = order.results.findIndex(r => r.name === e.data.name);
                    if (index !== -1) {
                        const cellValue = `${order.results[index].result}`;
                        const columnIndex = e.columns.findIndex((c: any) => c.caption === column.caption);
                        const cellIndex = e.cells.findIndex((cell: any) => cell.columnIndex === columnIndex);
                        this.renderer.setProperty(e.cells[cellIndex].cellElement, 'textContent', cellValue);
                    }
                }
            });
        }
    }

    private loadMicrobiologyResults(startDate: any, endDate: any, records: number) {
        if (this.microbiologyResultsVisible) {
            this.microbiologyResultsLoading = true;
            this.ordersService.getCumulativeResults(
                this.orderInfo.id,
                this.selectedClient,
                this.role === 'Physician' && this.user.data ? this.user.data.physicians : [],
                startDate.value ? startDate.value : startDate,
                endDate.value ? endDate.value : endDate, records,
                'microbiology',
            )
                .pipe(take(1)).subscribe((results: CumulativeResultsCollection[]) => {
                this.microbiologyResultsLoading = false;
                this.microbiologyResults = results;

                this.microbiologyResultsColumns = this.generateColumns(this.microbiologyResults);

                this.microbiologyRows = this.generateRows(results, this.microbiologyResultsColumns.map(c => c.columns).length);
            });
        }
    }

    public microbiologyRow(e: any) {
        e.cells.forEach((cell: any) => {
            if (cell.rowType && cell.columnIndex) {
                if (cell.rowType === 'data' && cell.columnIndex !== 0 && cell.cellElement !== undefined) {
                    this.renderer.setProperty(cell.cellElement, 'textContent', 'NOT ORDERED');
                }
            }
        });

        if (e.data) {
            e.columns.forEach((column: any) => {
                // @ts-ignore
              const order = this.getOrder(ObjectUtils.flatten(this.microbiologyResults.map(cr => cr.orders)), column.caption);
                if (order) {
                    const index = order.results.findIndex(r => r.name === e.data.name);
                    if (index !== -1 && (order.results[index] as MicrobiologyCumulativeResult).organisms) {
                        const cellValue = `${(order.results[index] as MicrobiologyCumulativeResult).organisms.map(o => o.name).join(', ')}`;
                        const columnIndex = e.columns.findIndex((c: any) => c.caption === column.caption);
                        const cellIndex = e.cells.findIndex((cell: any) => cell.columnIndex === columnIndex);
                        this.renderer.setProperty(e.cells[cellIndex].cellElement, 'textContent', cellValue);
                    }
                }
            });
        }
    }

    private getOrder(orders: CumulativeOrder[], code: string) {
        const index = orders.findIndex(o => o.code === code);
        return index !== -1 ? orders[index] : null;
    }

    public generateColumns(results: CumulativeResultsCollection[]) {
        let columns = [] as Array<{ dataField?: string, caption: Date | string, columns?: any[] }>;
        for (const result of results) {
            const column = {
                caption: new Date(result?.collectionDate ? result.collectionDate : ''),
                columns: result.orders?.sort((a: CumulativeOrderWrapper, b: CumulativeOrderWrapper) => {
                    // @ts-ignore
                    if (isNaN(a.code) || isNaN(b.code)) {
                        return -1;
                    }
                  // @ts-ignore
                    return +a.code - +b.code;
                }).map(o => {
                    return { caption: o.code };
                }),
            };
            columns.push(column);
        }

        // @ts-ignore
        columns = columns.slice().sort((a, b) => (a.caption as Date) - (b.caption as Date));
        columns.forEach(c => c.caption = Moment(c.caption).tz(tz).format('LL'));

        columns.unshift({
            dataField: 'name',
            caption: 'Tests',
        });

        return columns;
    }

    public generateRows(results: CumulativeResultsCollection[], columnsCount: number) {
        const testsNames: any[] = [];
        const rows = [];

        for (const result of results) {
            for (const order of result.orders ? result.orders : []) {
                const orderTests = (order.results as any[]).map(r => r.name);
                const unmatchedTests = orderTests.filter(ot => testsNames.findIndex(r => r === ot) === -1);
                testsNames.push(...unmatchedTests);
            }
        }

        for (const testName of testsNames) {
            const row = {
                name: testName,
            };

            for (let i = 1; i < columnsCount + 1; i++) {
                // @ts-ignore
              row[i.toString()] = 'NOT ORDERED';
            }
            rows.push(row);
        }

        return rows;
    }

    public recordsValueChanged(e: any) {
        this.records = e.value;
    }

    /*public cumulativeResults() {
        let queryString = '';
        queryString += `orderId=${this.orderInfo.id}&`;
        queryString += `records=${this.records}&`;
        if (this.clientSelector.value.value !== null) {
            queryString += `clientId=${this.clientSelector.value.value}&`;
        }
        if (this.startDateControl.value && this.startDateControl.value.value && this.startDateControl.value.value !== '') {
            queryString += `startDate=${this.dateHelper.formatDate(this.startDateControl.value.value)}&`;
        }
        if (this.endDateControl.value && this.endDateControl.value.value && this.endDateControl.value.value !== '') {
            queryString += `endDate=${this.dateHelper.formatDate(this.endDateControl.value.value)}&`;
        }

        this.isLoading = true;
        this.reportsService.generateCumulativeResults(queryString)
            .pipe(take(1))
            .subscribe((blob) => FileUtils.pdfRequestHandler(blob, this));
    }*/

    public onContentReady(e: any) {
        if (this.isPhoneOrTablet) {
            e.component.option('columns[0].fixed', true);
        }
    }

    public onZoomEnd(e: any) {
        this.visibleScroll = e.shift !== 0;
    }

    public customizeText(pointInfo: any) {
        return Moment(pointInfo.value).format('LL');
    }
}



interface CumulativeOrder {
    id: number;
    code: string;
    date: Date;
    results: ClinicalCumulativeResult[] | MicrobiologyCumulativeResult[] | ToxicologyCumulativeResult[];
}

interface ClinicalCumulativeResult {
    name: string;
    result: string;
    unit: string;
    resultType: string;
    released: boolean;
}

interface MicrobiologyCumulativeResult {
    name: string;
    result: string;
    organisms: Organism[];
}

interface Organism {
    name: string;
}

interface ToxicologyCumulativeResult {
    name: string;
    result: string;
}
